import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--spacing-4)',
  boxShadow: 'var(--shadow-sm)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--spacing-2) var(--spacing-3)',
  backgroundColor: 'var(--color-background)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
};

const primaryButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  backgroundColor: 'var(--color-accent)',
  border: 'none',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text-inverse)',
  fontWeight: 600,
  cursor: 'pointer',
};

function formatRelativeTime(isoTimestamp: string): string {
  // "5 min ago", "2 hr ago", "yesterday", "3 days ago" — friendly
  // relative format for the sync-status line. Anything older than
  // a week falls back to a date string so old failures don't say
  // "37 days ago" which is harder to read than the actual date.
  const t = new Date(isoTimestamp).getTime();
  if (!Number.isFinite(t)) return isoTimestamp;
  const secs = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(t).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Australia/Brisbane',
  });
}

function getToday() {
  return new Date().toISOString().slice(0, 10);
}

export function Expenses() {
  const queryClient = useQueryClient();
  const [locationId, setLocationId] = useState('');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(getToday());
  const [syncMessage, setSyncMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(
    null,
  );

  const { data: locations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn: api.getLocations,
  });

  // Xero tenants for the manual Sync button. If none connected, we
  // hide the button entirely (no point clicking it).
  const { data: xeroStatus } = useQuery({
    queryKey: ['xero-status'],
    queryFn: () => api.xeroStatus(),
  });
  const xeroTenants = xeroStatus?.tenants ?? [];

  // Per-tenant sync state — drives the "last synced X min ago" line
  // under the Sync button. Refetches every 30s so the scheduled run
  // surfaces without a reload.
  const { data: syncStatuses = [] } = useQuery({
    queryKey: ['expense-sync-status'],
    queryFn: api.getExpenseSyncStatus,
    refetchInterval: 30_000,
    enabled: xeroTenants.length > 0,
  });

  // Square posts its payouts and fees to Xero already reconciled, so they
  // arrive daily whatever anyone does. Everything else — rent, loans, telco,
  // energy — waits in Xero's Reconcile tab as a bank statement line, which the
  // API does not expose at all. A gap therefore looks like a healthy ledger
  // with fresh Square rows in it, which is how 16 days went unnoticed between
  // 16 Sep and 2 Oct 2026. This surfaces the one date that actually moves.
  const reconciliation = useMemo(() => {
    const date = syncStatuses[0]?.latest_reconciled_expense_date ?? null;
    const days = syncStatuses[0]?.days_since_reconciled_expense ?? null;
    if (!date || days == null) {
      return { date: null, headline: '', detail: '', border: '', background: '' };
    }
    const ago = days === 0 ? 'today' : days === 1 ? 'yesterday' : `${days} days ago`;
    const stale = days >= 4;
    const bad = days >= 8;
    return {
      date,
      headline: stale
        ? `Expenses may be missing — nothing reconciled since ${date} (${ago})`
        : `Last reconciled expense: ${date} (${ago})`,
      detail: stale
        ? 'Transactions sitting unreconciled in Xero never reach this page. Reconcile them in Xero and the next hourly sync picks them up. Square fees and payouts are excluded from this check because they reconcile themselves.'
        : 'Excludes Square, which reconciles itself and would hide a gap in everything else.',
      border: bad ? 'var(--color-error, #dc3545)' : stale ? '#d9a843' : 'var(--color-border)',
      background: bad
        ? 'rgba(220,53,69,0.08)'
        : stale
          ? 'rgba(217,168,67,0.12)'
          : 'var(--color-surface-elevated)',
    };
  }, [syncStatuses]);

  const syncMutation = useMutation({
    mutationFn: api.syncExpensesFromXero,
    onSuccess: (res) => {
      setSyncMessage({
        kind: 'ok',
        text:
          `Pulled ${res.pulled_line_items} line item${res.pulled_line_items === 1 ? '' : 's'} · ` +
          `${res.inserted} inserted · ${res.updated} updated · ${res.unchanged} unchanged`,
      });
      queryClient.invalidateQueries({ queryKey: ['expenses-module'] });
      queryClient.invalidateQueries({ queryKey: ['expense-sync-status'] });
    },
    onError: (err: any) => {
      setSyncMessage({ kind: 'error', text: err?.message || 'Sync failed' });
    },
  });

  const { data: expenseData, isLoading } = useQuery({
    queryKey: ['expenses-module', locationId, startDate, endDate],
    queryFn: () =>
      api.getExpensesReport({
        ...(locationId ? { locationId } : {}),
        startDate,
        endDate,
      }),
  });

  const expenses = expenseData?.expenses || [];
  const total = Number(expenseData?.total || 0);
  const categoryTotals = useMemo(() => {
    const map = new Map<string, number>();
    for (const expense of expenses) {
      const key = expense.category || 'Uncategorised';
      map.set(key, (map.get(key) || 0) + Number(expense.amount || 0));
    }
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [expenses]);

  return (
    <div className="expenses-page">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 'var(--spacing-4)',
          marginBottom: 'var(--spacing-4)',
        }}
      >
        <div>
          <h1>Expenses</h1>
          <p style={{ color: 'var(--color-text-muted)', marginTop: 'var(--spacing-2)' }}>
            Every expense comes from Xero. Reconcile a transaction there and the next hourly sync
            brings it in.
          </p>
        </div>
      </div>

      {/* Xero sync card — only renders when there's a connected
          tenant. Manual button on top of the hourly scheduler that
          runs 06:00–18:00 Brisbane (booted in apps/backend/src/index.ts).
          Status line under the button shows last-run details for each
          connected tenant; polls every 30s. */}
      {xeroTenants.length > 0 ? (
        <div style={{ ...cardStyle, marginBottom: 'var(--spacing-4)' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 'var(--spacing-3)',
              flexWrap: 'wrap',
            }}
          >
            <div>
              <h2 style={{ margin: 0 }}>Sync expenses from Xero</h2>
              <p
                style={{
                  color: 'var(--color-text-muted)',
                  marginTop: 4,
                  marginBottom: 0,
                  fontSize: '13px',
                }}
              >
                Runs automatically every hour from 06:00 to 18:00 Brisbane. Click below to pull
                right now.
              </p>
            </div>
            <button
              onClick={() => {
                setSyncMessage(null);
                syncMutation.mutate({ tenantId: xeroTenants[0].tenantId });
              }}
              disabled={syncMutation.isPending}
              style={{
                ...primaryButtonStyle,
                opacity: syncMutation.isPending ? 0.6 : 1,
              }}
            >
              {syncMutation.isPending ? 'Syncing…' : '⟳ Sync with Xero'}
            </button>
          </div>
          {syncMessage ? (
            <p
              style={{
                marginTop: 'var(--spacing-3)',
                marginBottom: 0,
                fontSize: '13px',
                color:
                  syncMessage.kind === 'ok'
                    ? 'var(--color-success, #28a745)'
                    : 'var(--color-error, #dc3545)',
              }}
            >
              {syncMessage.text}
            </p>
          ) : null}
          {reconciliation.date ? (
            <div
              style={{
                marginTop: 'var(--spacing-3)',
                padding: 'var(--spacing-3)',
                borderRadius: 'var(--radius-default)',
                border: `1px solid ${reconciliation.border}`,
                backgroundColor: reconciliation.background,
                fontSize: '13px',
              }}
            >
              <strong>{reconciliation.headline}</strong>
              <div style={{ marginTop: 4, color: 'var(--color-text-muted)' }}>
                {reconciliation.detail}
              </div>
            </div>
          ) : null}

          {syncStatuses.length > 0 ? (
            <div
              style={{
                marginTop: 'var(--spacing-3)',
                paddingTop: 'var(--spacing-3)',
                borderTop: '1px solid var(--color-border)',
                fontSize: '13px',
              }}
            >
              {syncStatuses.map((s) => (
                <div key={s.tenant_id} style={{ marginBottom: 2 }}>
                  <strong>{s.tenant_name || s.tenant_id.slice(0, 8)}</strong>{' '}
                  {s.last_run_at ? (
                    <>
                      <span style={{ color: 'var(--color-text-muted)' }}>
                        · last sync {formatRelativeTime(s.last_run_at)}
                      </span>
                      <span
                        style={{
                          marginLeft: 6,
                          color:
                            s.last_status === 'error'
                              ? 'var(--color-error, #dc3545)'
                              : 'var(--color-text-muted)',
                        }}
                      >
                        ({s.last_trigger}
                        {s.last_status === 'error'
                          ? ` · failed: ${s.last_error_message ?? 'unknown error'}`
                          : ` · ${s.pulled_line_items} pulled, ${s.inserted} inserted`}
                        )
                      </span>
                    </>
                  ) : (
                    <span style={{ color: 'var(--color-text-muted)' }}>· never synced</span>
                  )}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 'var(--spacing-4)',
        }}
      >
        <section style={{ display: 'grid', gap: 'var(--spacing-4)' }}>
          <div style={cardStyle}>
            <h2 style={{ marginBottom: 'var(--spacing-3)' }}>Summary</h2>
            <p style={{ color: 'var(--color-text-muted)', marginBottom: 'var(--spacing-2)' }}>
              Total expenses in range
            </p>
            <p
              style={{
                fontSize: 'var(--font-size-3xl)',
                fontWeight: 700,
                color: 'var(--color-error)',
              }}
            >
              ${total.toFixed(2)}
            </p>
            <div
              style={{ display: 'grid', gap: 'var(--spacing-2)', marginTop: 'var(--spacing-4)' }}
            >
              {categoryTotals.map(([category, amount]) => (
                <div
                  key={category}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    color: 'var(--color-text-muted)',
                  }}
                >
                  <span>{category}</span>
                  <strong style={{ color: 'var(--color-text)' }}>${amount.toFixed(2)}</strong>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section style={cardStyle}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 'var(--spacing-3)',
              marginBottom: 'var(--spacing-3)',
              flexWrap: 'wrap',
            }}
          >
            <h2 style={{ margin: 0 }}>Expense Ledger</h2>
            <div
              style={{
                display: 'flex',
                gap: 'var(--spacing-2)',
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              <select
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
                style={{ ...inputStyle, width: '160px' }}
              >
                <option value="">All locations</option>
                {locations.map((location: any) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ ...inputStyle, width: '150px' }}
              />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{ ...inputStyle, width: '150px' }}
              />
            </div>
          </div>

          {isLoading ? (
            <p style={{ color: 'var(--color-text-muted)' }}>Loading expenses...</p>
          ) : expenses.length === 0 ? (
            <p style={{ color: 'var(--color-text-muted)' }}>No expenses recorded for this range.</p>
          ) : (
            <div style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
              {expenses.map((expense: any) => (
                <div
                  key={expense.id}
                  style={{
                    padding: 'var(--spacing-3)',
                    backgroundColor: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-default)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 'var(--spacing-3)',
                    }}
                  >
                    <div>
                      <strong>{expense.name}</strong>
                      <p
                        style={{
                          color: 'var(--color-text-muted)',
                          marginTop: 'var(--spacing-1)',
                        }}
                      >
                        {expense.category} · {expense.date} ·{' '}
                        {locations.find((loc: any) => loc.id === expense.location_id)?.name ||
                          `Location #${expense.location_id}`}
                      </p>
                    </div>
                    <strong>${Number(expense.amount || 0).toFixed(2)}</strong>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
